import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserLogged } from '../../../../hooks/useUserLogged';
import { useSalePoints } from '../../../../hooks/useSalePoints';
import { useDifferimentInvoicePrint } from '../../../../hooks/useDifferimentInvoices';
import { differimentKeys } from '../../../../hooks/useDifferiments';
import { differimentDetailKeys } from '../../../../hooks/useDifferimentPayments';
import { differimentInvoiceKeys } from '../../../../hooks/useDifferimentInvoices';
import {
  useCreateManualDifferimentPayment,
  useDeleteDifferimentPayment,
  useGenerateDifferimentPaymentQr,
  useIssueDifferimentInvoiceBatch,
  useMarkDifferimentPayment,
} from '../../../../hooks/useDifferimentPayments';

export const useDifferimentPaymentDialog = (detail, onRefresh) => {
  const queryClient = useQueryClient();
  const pendingInstallments = useMemo(() => Number(detail?.pending_differiment || 0), [detail]);
  const [installmentsToPay, setInstallmentsToPay] = useState('');
  const [cuotaPura, setCuotaPura] = useState('');
  const [qrDialog, setQrDialog] = useState(null); // {qr_data, qr_image_base64}
  const [invoiceDialog, setInvoiceDialog] = useState(null); // { paymentUuid }
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedSalePointId, setSelectedSalePointId] = useState('');
  const [makeElectronicInvoice, setMakeElectronicInvoice] = useState(true);
  const [invoiceError, setInvoiceError] = useState(null);

  const { uuid: userUuid, branch = [] } = useUserLogged();
  const {
    data: salePoints = [],
    isLoading: salePointsLoading,
  } = useSalePoints();

  const invalidateDifferimentsQueries = () => {
    queryClient.invalidateQueries({ queryKey: differimentKeys.all });
    if (detail?.uuid_contract) {
      queryClient.invalidateQueries({ queryKey: differimentDetailKeys.detail(detail.uuid_contract) });
    }
    queryClient.invalidateQueries({ queryKey: differimentInvoiceKeys.all });
  };

  const selectedSalePoint = useMemo(
    () => salePoints.find((point) => point.uuid === selectedSalePointId) || null,
    [salePoints, selectedSalePointId]
  );

  useEffect(() => {
    if (salePoints.length > 0 && !selectedSalePointId) {
      const defaultSalePoint = salePoints.find((point) => point.number === 9) || salePoints[0];
      setSelectedSalePointId(defaultSalePoint.uuid);
    }
  }, [salePoints, selectedSalePointId]);

  useEffect(() => {
    if (selectedSalePoint && selectedSalePoint.invoice === false && makeElectronicInvoice) {
      setMakeElectronicInvoice(false);
    }
  }, [selectedSalePoint, makeElectronicInvoice]);

  const markPaidMutation = useMarkDifferimentPayment({
    onSuccess: () => {
      invalidateDifferimentsQueries();
      if (typeof onRefresh === 'function') onRefresh();
    },
  });

  const deleteMutation = useDeleteDifferimentPayment({
    onSuccess: () => {
      invalidateDifferimentsQueries();
      if (typeof onRefresh === 'function') onRefresh();
    },
  });

  const totalFromForm = useMemo(() => {
    if (!installmentsToPay || !cuotaPura) return 0;
    return Number(installmentsToPay) * Number(cuotaPura);
  }, [installmentsToPay, cuotaPura]);

  const generateQrMutation = useGenerateDifferimentPaymentQr({
    onSuccess: (data) => {
      setQrDialog({
        qr_data: data?.qr_data,
        qr_image_base64: data?.qr_image_base64,
      });
    },
  });

  const manualPaymentMutation = useCreateManualDifferimentPayment({
    onSuccess: () => {
      invalidateDifferimentsQueries();
      if (typeof onRefresh === 'function') {
        onRefresh();
      }
    },
  });
  const isSubmitting = manualPaymentMutation.isPending || manualPaymentMutation.isLoading;

  const handleSubmit = async () => {
    if (!detail?.uuid_contract) return;
    const installments = Number(installmentsToPay);
    const cuota = Number(cuotaPura);
    if (!installments || installments <= 0) return;
    if (pendingInstallments && installments > pendingInstallments) return;
    if (!cuota || cuota <= 0) return;

    const amount = installments * cuota;
    try {
      await manualPaymentMutation.mutateAsync({
        contractUuid: detail.uuid_contract,
        amount,
        installmentsToPay: installments,
        cuotaPura: cuota,
      });
    } catch (err) {
      // silencioso
    }
  };

  const invoiceMutation = useIssueDifferimentInvoiceBatch({
    onSuccess: (data) => {
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        const firstError = data.errors[0];
        setInvoiceError(firstError?.error || 'No se pudo facturar el pago.');
        return;
      }
      setInvoiceDialog(null);
      setInvoiceError(null);
      invalidateDifferimentsQueries();
      if (typeof onRefresh === 'function') onRefresh();
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'No se pudo facturar el pago.';
      setInvoiceError(message);
    },
  });

  const printInvoiceMutation = useDifferimentInvoicePrint();

  const openInvoiceDialog = (paymentUuid) => {
    setInvoiceDialog({ paymentUuid });
    setInvoiceError(null);
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    if (salePoints.length > 0) {
      const defaultSalePoint = salePoints.find((point) => point.number === 9) || salePoints[0];
      setSelectedSalePointId(defaultSalePoint.uuid);
    }
  };

  const submitInvoice = () => {
    if (!invoiceDialog?.paymentUuid) return;
    if (!userUuid) {
      setInvoiceError('No se encontró el usuario autenticado.');
      return;
    }
    if (!branch?.[0]?.uuid_branch) {
      setInvoiceError('No se encontró la sucursal del usuario.');
      return;
    }
    if (!selectedSalePointId) {
      setInvoiceError('Debe seleccionar un punto de venta.');
      return;
    }
    setInvoiceError(null);
    invoiceMutation.mutate({
      payments: [invoiceDialog.paymentUuid],
      uuid_user: userUuid,
      uuid_branch: branch?.[0]?.uuid_branch,
      uuid_sale_point: selectedSalePointId,
      make_electronic_invoice: makeElectronicInvoice,
      invoice_date: invoiceDate,
    });
  };

  return {
    state: {
      installmentsToPay,
      cuotaPura,
      isSubmitting,
      totalFromForm,
      pendingInstallments,
      qrDialog,
      isGeneratingQr: generateQrMutation.isLoading,
      invoiceDialog,
      invoiceDate,
      selectedSalePointId,
      makeElectronicInvoice,
      salePoints,
      salePointsLoading,
      invoiceError,
      isInvoicing: invoiceMutation.isPending || invoiceMutation.isLoading,
      selectedSalePoint,
      isPrintingInvoice: printInvoiceMutation.isLoading,
    },
    actions: {
      setInstallmentsToPay: (value) => {
        if (value === '') {
          setInstallmentsToPay('');
          return;
        }
        const numeric = Number(value);
        if (pendingInstallments && numeric > pendingInstallments) {
          setInstallmentsToPay(String(pendingInstallments));
          return;
        }
        setInstallmentsToPay(value);
      },
      setCuotaPura,
      closeQrDialog: () => setQrDialog(null),
      handleSubmit,
      markPaid: (uuid) => markPaidMutation.mutate(uuid),
      deletePayment: (uuid) => deleteMutation.mutate(uuid),
      generateQr: (uuid) => generateQrMutation.mutate(uuid),
      openInvoiceDialog,
      closeInvoiceDialog: () => setInvoiceDialog(null),
      setInvoiceDate,
      setSelectedSalePointId,
      setMakeElectronicInvoice,
      submitInvoice,
      printInvoice: (invoiceUuid) => {
        if (!invoiceUuid) return;
        printInvoiceMutation.mutate(invoiceUuid);
      },
    },
  };

};

export default useDifferimentPaymentDialog;
